import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { apiClient, ApiClientError, gapi } from './client'
import type { MyProfile } from '../types/my-profile'

/**
 * useMyProfile — 拉 `GET {g}/profile` 当前用户完整 profile。
 *
 * 与 `useCurrentUser` 区别：
 *  - `useCurrentUser` 走 supabase-js + PostgREST 直读 `profiles` 表（轻量、用于
 *    路由守卫 / 顶栏头像）
 *  - `useMyProfile` 走 server-side RPC，返完整字段 + 派生字段（username_confirmed_at /
 *    password_changed_at / vip / wallet_balance_coin）。**仅在「个人资料编辑页 / 我的页头部」用**，
 *    其他场景请优先用 `useCurrentUser` 避免每页都打 RPC。
 *
 * 缓存 key 与 `useCurrentUser` 隔离：`['profile', 'me']`
 *
 * Spec: `docs/api-list/user/profile-settings.md` §3
 */
export function useMyProfile(
  opts: { enabled?: boolean } = {},
): UseQueryResult<MyProfile, ApiClientError> {
  const enabled = opts.enabled ?? true
  return useQuery<MyProfile, ApiClientError>({
    queryKey: ['profile', 'me'] as const,
    queryFn: () => apiClient.get<MyProfile>(gapi('/profile')),
    enabled,
    staleTime: 30_000,
  })
}

/** updateUsername 入参 */
export type UpdateUsernamePayload = {
  new_username: string
}

/** updateUsername 成功返回（spec §3 未明确返 body shape，按 envelope-flat 双兼容） */
export type UpdateUsernameResp = {
  user_id: string
  username: string
  /** 更新后剩余次数 */
  remaining_changes_in_window?: number
  /** 若已锁定，返回下次解锁时间 */
  can_change_again_at?: string | null
}

/**
 * updateUsername — `PATCH {g}/profile/username`，仅允许修改一次（2026-06-11 规则）。
 *
 * 错误码（spec §3）：
 *  - `USERNAME_CHANGE_LOCKED`：用户名已锁定，仅允许修改一次
 *  - `USERNAME_TAKEN_IN_AGENT`：(agent_id, username) 已存在
 *  - `INVALID_USERNAME`：包含特殊字符 / 长度不符
 *
 * 服务端校验：长度 3-30、ASCII、大小写归一（KI-30 → 自动转小写）。
 *
 * Returns `Promise<UpdateUsernameResp>` — 让 caller 自己 toast / setError。
 * 与 react-query 一起用：`useMutation({ mutationFn: updateUsername })`。
 */
export async function updateUsername(newUsername: string): Promise<UpdateUsernameResp> {
  return apiClient.patch<UpdateUsernameResp>(gapi('/profile/username'), {
    new_username: newUsername,
  } satisfies UpdateUsernamePayload)
}

/** 钩子封装，自动失效 ['profile', 'me'] 缓存 */
export function useUpdateUsername(): UseMutationResult<
  UpdateUsernameResp,
  ApiClientError,
  string
> {
  const qc = useQueryClient()
  return useMutation<UpdateUsernameResp, ApiClientError, string>({
    mutationFn: updateUsername,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['profile', 'me'] })
      void qc.invalidateQueries({ queryKey: ['auth', 'current-user'] })
    },
  })
}

/** useKeepUsername 成功返回 */
export type KeepUsernameResp = {
  ok: boolean
}

/**
 * useKeepUsername — `POST {g}/profile/username/keep`，保留当前用户名（不改名）。
 *
 * 新注册用户 `username_confirmed_at` 为 null 时，可以选择"保留"当前系统分配的用户名
 * 而不修改，调用后 `username_confirmed_at` 更新为当前时间。
 *
 * 请求体为空，响应 `{ok: true}`。
 *
 * 成功后自动 invalidate `['profile', 'me']` 缓存。
 */
export function useKeepUsername(): UseMutationResult<KeepUsernameResp, ApiClientError, void> {
  const qc = useQueryClient()
  return useMutation<KeepUsernameResp, ApiClientError, void>({
    mutationFn: () => apiClient.post<KeepUsernameResp>(gapi('/profile/username/keep'), {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['profile', 'me'] })
    },
  })
}

/** updatePhone 入参 */
export type UpdatePhonePayload = {
  /** E.164 格式，例：'+8613800138000' */
  new_phone: string
}

export type UpdatePhoneResp = {
  user_id: string
  phone_real: string
}

/**
 * updatePhone — `PATCH {g}/profile/phone`，MVP 直接更新（不发短信验证码）。
 *
 * 错误码（spec §3）：
 *  - `INVALID_PHONE`：非 E.164 格式
 *  - `PHONE_TAKEN_IN_AGENT`：(agent_id, phone) 已被占用
 */
export async function updatePhone(newPhone: string): Promise<UpdatePhoneResp> {
  return apiClient.patch<UpdatePhoneResp>(gapi('/profile/phone'), {
    new_phone: newPhone,
  } satisfies UpdatePhonePayload)
}

export function useUpdatePhone(): UseMutationResult<UpdatePhoneResp, ApiClientError, string> {
  const qc = useQueryClient()
  return useMutation<UpdatePhoneResp, ApiClientError, string>({
    mutationFn: updatePhone,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['profile', 'me'] })
      void qc.invalidateQueries({ queryKey: ['auth', 'current-user'] })
    },
  })
}

/** setAvatar 入参 */
export type SetAvatarPayload = {
  /** 走 {g}/upload/big/* 完成后的 file_id（imgproxy URL 由 server 拼） */
  file_id: string
}

export type SetAvatarResp = {
  user_id: string
  /** server 拼好的 imgproxy URL：`<imgproxy>/resize:256:256/plain/<gapi_decrypt>/<file_id>` */
  avatar_url: string
}

/**
 * setAvatar — `PUT {g}/profile/avatar`，把已上传的 file_id 设为头像。
 *
 * 上传流程：
 *  1. 走 `{g}/upload/big/init` + chunks + finalize 拿 file_id
 *     （或 `{g}/upload/small` 小文件路径）
 *  2. 调本 endpoint 设置 `profiles.avatar_url`
 *
 * 限制（spec §3）：≤ 5MB、mime 必须 image/*、覆盖既有 `__system__/avatar.*`。
 */
export async function setAvatar(fileId: string): Promise<SetAvatarResp> {
  return apiClient.put<SetAvatarResp>(gapi('/profile/avatar'), {
    file_id: fileId,
  } satisfies SetAvatarPayload)
}

export function useSetAvatar(): UseMutationResult<SetAvatarResp, ApiClientError, string> {
  const qc = useQueryClient()
  return useMutation<SetAvatarResp, ApiClientError, string>({
    mutationFn: setAvatar,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['profile', 'me'] })
      void qc.invalidateQueries({ queryKey: ['auth', 'current-user'] })
    },
  })
}
